/**
 * The objective list in the corner of the HUD: what the profile asks of the player and
 * how far along they are. Updates only on discrete zone events; a polite live region
 * so progress is announced to assistive tech without interrupting.
 */

import { levelById, levelPosition } from '../game/progression';
import { useGameStore } from '../store/gameStore';

export function ObjectiveHud() {
  const objectiveProgress = useGameStore((store) => store.objectiveProgress);
  const profile = useGameStore((store) => store.profile);
  const currentLevelId = useGameStore((store) => store.currentLevelId);

  // The store carries every level's objectives from the first frame — that is what keeps
  // one completed when the player crosses back into a maze they have already finished. The
  // HUD shows the one maze they are standing in, in the order the profile authored it.
  // With no level resolved there is nothing to filter by, and what is in the store is all
  // there is to show.
  const level = profile && currentLevelId ? levelById(profile, currentLevelId) : null;
  const objectives = level
    ? level.objectives.map((objective) => objectiveProgress[objective.id]).filter((it) => it != null)
    : Object.values(objectiveProgress);
  if (objectives.length === 0) return null;

  // Where in the run they are. Three mazes made of the same hedges under the same moon are
  // not told apart from the inside, so without this a player who has crossed twice cannot
  // say whether the next gate ends the run. Shown only for a run that has more than one
  // level: "Level 1 of 1" is a number with nothing to say. It sits inside the same live
  // region as the objective, so crossing announces the new maze and its task together.
  const position = profile && currentLevelId ? levelPosition(profile, currentLevelId) : null;

  return (
    <section className="objective-hud" role="status" aria-live="polite">
      {position && (position.total === null || position.total > 1) && (
        <p className="objective-hud__level">
          {/* No total in an endless run: "of 3" would promise an end that is not coming. */}
          {position.total === null
            ? `Level ${position.step}`
            : `Level ${position.step} of ${position.total}`}
        </p>
      )}
      {objectives.map((objective) => {
        // Nothing to count is not the same as none of them done. An objective that is a
        // place rather than a pile of boxes — walking out of the maze — requires no
        // objects at all, and "0/0" beside it reads as a task that cannot be finished.
        const tally = objective.requiredIds.length > 0;

        return (
          <p
            key={objective.objectiveId}
            className={
              objective.isComplete
                ? 'objective-hud__item objective-hud__item--complete'
                : 'objective-hud__item'
            }
          >
            {(objective.isComplete || tally) && (
              <>
                <span className="objective-hud__count">
                  {objective.isComplete
                    ? 'Done'
                    : `${objective.depositedIds.length}/${objective.requiredIds.length}`}
                </span>{' '}
              </>
            )}
            <span>{objective.description}</span>
          </p>
        );
      })}
    </section>
  );
}
